import { useIonRouter } from "@ionic/react";
import { useEffect, useState } from "react";

import { ActionBar } from "../../components/ActionBar";
import { Hero } from "../../components/Hero";
import { I } from "../../components/icons";
import { Notice } from "../../components/Notice";
import { QuestionOptions } from "../../components/Question";
import { NavRow, RowGroup } from "../../components/Rows";
import { Segmented } from "../../components/Segmented";
import { Sheet } from "../../components/Sheet";
import { dayLabel, todayStr } from "../../domain/date";
import { older } from "../../domain/eating";
import { voiceClipsLine } from "../../domain/playerView";
import { voiceText } from "../../domain/voiceWords";
import { answeredCount, programmeLine, retestDue } from "../../domain/trainingQuestions";
import type { Programme } from "../../domain/types";
import { useKaya, usePlayerMode, useTrainingDevice } from "../../store/kayaStore";
import { doseIndex, sessionKeyFor } from "../../domain/plan";
import { buildQueue, queueMinutes } from "../../domain/queue";
import { clipsOnPhone, onVoicesChanged, voice as speaker } from "../../lib/voice";
import { setTrainingProfile } from "../../store/toggles";
import SettingsPage from "./SettingsPage";

const PROGRAMME_OPTS: { value: Programme; label: string; sub: string }[] = [
  { value: "kaya", label: "Kaya", sub: "Core & upper, intervals and slow core, twice a week, and a rest day" },
  { value: "steady", label: "Steady", sub: "Strength & balance three days, walks most days, all sitting or at the counter" },
];

/**
 * You › Training (board YouTraining; ADR-053): the programme and its one line, the answers and the
 * retest, and the player on this phone. The programme and answers are `profile.training` and sync
 * (a linked owner can set them, "set by …"); Simple player, voice and language are this phone's
 * (`LocalMeta`), for `textSize`'s reason — the right player is the person in front of the screen.
 */
export default function TrainingSettings() {
  const S = useKaya((s) => s.S);
  const meta = useKaya((s) => s.meta);
  const update = useKaya((s) => s.update);
  const setPlayerPrefs = useKaya((s) => s.setPlayerPrefs);
  const router = useIonRouter();
  const [sheet, setSheet] = useState<"programme" | "voice" | null>(null);

  const t = S.profile.training;
  const programme: Programme = t?.programme ?? (older(S) ? "steady" : "kaya");
  const device = useTrainingDevice();
  const pm = usePlayerMode();
  const todayKey = sessionKeyFor(S, todayStr());
  const shortKey = todayKey === "SB" || todayKey === "GC" || todayKey === "CH" ? todayKey : "SB";
  const shortMin = queueMinutes(S, buildQueue(S, shortKey, doseIndex(S, shortKey), { device, short: true }), pm.simple);
  const count = answeredCount(S, new Date(), { gate: meta.trainingSafety?.gate === "doctor", safetyAnswered: !!meta.trainingSafety });
  const due = retestDue(S);
  const held = meta.trainingSafety?.gate === "doctor";
  const voice = meta.voice ?? t?.voice ?? false;
  const lang = meta.lang ?? t?.lang ?? "en";
  const simple = meta.simplePlayer ?? t?.simple ?? false;

  const answers = count
    ? `${count.n} of ${count.of} answered${due ? ` · retest ${due <= todayStr() ? "due now" : `due ${dayLabel(due)}`}` : ""}`
    : meta.trainingDraft
      ? "Carry on where you left off"
      : "Not answered yet";

  return (
    <SettingsPage title="Training">
      <Hero
        label={`Your programme${t?.at ? ` · since ${dayLabel(todayStr(new Date(t.at)))}` : ""}${t?.setBy ? ` · set by ${t.setBy}` : ""}`}
        title={programme === "steady" ? "Steady" : "Kaya"}
        line={programmeLine(S)}
      />

      {held ? (
        <Notice
          tone="caution"
          primary={{ label: "My doctor said yes", onClick: () => router.push("/train/questions/before?from=you", "forward") }}
        >
          Your plan is held at easy walks and the gentle sessions until a doctor says more.
        </Notice>
      ) : null}

      <RowGroup heading="Plan">
        <NavRow icon={I.dumbbell} title="Programme" value={programme === "steady" ? "Steady" : "Kaya"} onClick={() => setSheet("programme")} />
        <NavRow icon={I.list} title="Plan my exercise" subtitle={answers} to="/train/questions?from=you" />
        {programme === "steady" ? (
          // the same figure Train shows for today's template (or Strength & balance on a walk or rest day)
          <NavRow kind="value" icon={I.clock} title="Short version" subtitle={`${shortMin} minutes · for a bad day; it counts as done`} />
        ) : null}
      </RowGroup>

      <RowGroup heading="The player on this phone">
        <NavRow
          kind="toggle"
          title="Simple player"
          subtitle="Bigger buttons, Stop in words, moves that go on by themselves"
          checked={simple}
          onChange={(v) => setPlayerPrefs({ simplePlayer: v })}
        />
        <NavRow
          icon={I.bell}
          title="Voice"
          value={voice ? `On · ${lang === "hi" ? "हिंदी" : "English"}` : "Off"}
          onClick={() => setSheet("voice")}
        />
      </RowGroup>
      <p className="k-field__hint">
        The player settings stay on this phone. Your programme and answers sync
        {t?.setBy ? `, and ${t.setBy} can change them from their phone` : ""}.
      </p>

      {sheet === "programme" ? (
        <ProgrammeSheet
          value={programme}
          onClose={() => setSheet(null)}
          onSave={(p) => {
            // her own choice now: the "set by" goes with it
            update((d) => {
              setTrainingProfile(d, { programme: p, setBy: undefined });
            });
            setSheet(null);
          }}
        />
      ) : null}

      {sheet === "voice" ? (
        <VoiceSheet
          voice={voice}
          lang={lang}
          onVoice={(v) => setPlayerPrefs({ voice: v })}
          onLang={(v) => setPlayerPrefs({ lang: v })}
          onClose={() => setSheet(null)}
        />
      ) : null}
    </SettingsPage>
  );
}

function ProgrammeSheet({ value, onClose, onSave }: { value: Programme; onClose: () => void; onSave: (p: Programme) => void }) {
  const [p, setP] = useState<Programme>(value);
  return (
    <Sheet
      large
      title="Programme"
      sub="Your answers stay; the week follows the programme."
      onClose={onClose}
      footer={
        <ActionBar
          inSheet
          secondary={{ label: "Cancel", onClick: onClose }}
          primary={{ label: "Save", onClick: () => onSave(p), disabled: p === value, reason: "No change yet" }}
        />
      }
    >
      <QuestionOptions labelledBy="" single options={PROGRAMME_OPTS} chosen={[p]} onPick={setP} />
    </Sheet>
  );
}

/**
 * Voice in the player (board YouTrainingVoice; ADR-055): on or off, the language of the player's
 * words and voice, and an honest line about what will speak — the recorded clips this phone has,
 * or with none the phone's own voice, or beeps only. "Try the voice" says one cue the way the
 * player would, through the same fallbacks.
 */
function VoiceSheet({
  voice,
  lang,
  onVoice,
  onLang,
  onClose,
}: {
  voice: boolean;
  lang: "en" | "hi";
  onVoice: (v: boolean) => void;
  onLang: (v: "en" | "hi") => void;
  onClose: () => void;
}) {
  const [clips, setClips] = useState(0);
  const [phone, setPhone] = useState(() => speaker.phoneVoice(lang));
  const [tried, setTried] = useState(0);

  useEffect(() => {
    let live = true;
    void clipsOnPhone(lang).then((n) => {
      if (live) setClips(n);
    });
    setPhone(speaker.phoneVoice(lang));
    // the phone's voice list fills in late
    const off = onVoicesChanged(() => setPhone(speaker.phoneVoice(lang)));
    return () => {
      live = false;
      off();
    };
  }, [lang, tried]);

  const sample = `“${voiceText(lang, "next")} · ${voiceText(lang, "sitstand")}”`;

  return (
    <Sheet
      title="Voice in the player"
      sub="Says the next move, the rest, “switch sides” and the count. This phone only."
      onClose={onClose}
      footer={<ActionBar inSheet primary={{ label: "Done", onClick: onClose }} />}
    >
      <div className="you-stack">
        <div className="k-rows">
          <NavRow kind="toggle" title="Voice" subtitle="In the player only" checked={voice} onChange={onVoice} />
        </div>
        <Segmented<"en" | "hi">
          ariaLabel="The player’s language"
          value={lang}
          options={[
            { value: "en", label: "English" },
            { value: "hi", label: <span lang="hi">हिंदी</span> },
          ]}
          onChange={onLang}
        />
        <p className="k-field__hint">The player’s words and voice. The rest of Kaya stays in English.</p>
        <div className="k-rows">
          <NavRow kind="value" icon={I.bell} title="Voice clips" subtitle={voiceClipsLine(clips, phone)} />
          <NavRow
            icon={I.play}
            title="Try the voice"
            subtitle={<span lang={lang}>{sample}</span>}
            onClick={() => {
              void speaker.say(["next", "sitstand"], lang).then(() => setTried((n) => n + 1));
            }}
          />
        </div>
        <p className="k-field__hint">Clips download the first time they are needed.</p>
      </div>
    </Sheet>
  );
}
